/**
 * Centralized query key factory for React Query
 * This ensures consistent query keys across the application
 */

export const queryKeys = {
  // User-related queries
  user: {
    all: ["users"] as const,
    current: ["app-user-data"] as const,
    settings: ["userSettings"] as const,
    profile: (userId: string) => ["userData", userId] as const,
    notifications: (estateId: number) => ["userNotifications", estateId] as const,
  },

  // Person/deceased user queries
  person: {
    all: ["estates"] as const,
    single: (estateId: number) => ["person", estateId] as const,
    metadata: (estateId: number) => ["personMetadata", estateId] as const,
    invitations: (estateId: number) => ["personInvitations", estateId] as const,
  },

  // Task-related queries
  tasks: {
    all: ["tasks"] as const,
    person: (estateId?: number) => ["personTasks", estateId] as const,
    single: (taskId: number, estateId?: number) => ["personTask", taskId, estateId] as const,
    featured: ["featuredTasks"] as const,
    buckets: ["buckets"] as const,
    templates: ["taskTemplates"] as const,
  },

  // Document-related queries
  documents: {
    all: ["documents"] as const,
    person: (estateId: number) => ["personDocuments", estateId] as const,
    single: (documentId: string) => ["document", documentId] as const,
    metadata: (documentId: string) => ["documentMetadata", documentId] as const,
    types: ["documentTypes"] as const,
  },

  // Contact-related queries
  contacts: {
    all: ["contacts"] as const,
    person: (estateId: number) => ["personContacts", estateId] as const,
    single: (contactId: number) => ["contact", contactId] as const,
  },

  // Estate-related queries
  estate: {
    all: ["estate"] as const,
    items: (estateId: number) => ["estateItems", estateId] as const,
    summary: (estateId: number) => ["estateSummary", estateId] as const,
    activityLog: (estateId: number) => ["estate-activity-log", estateId] as const,
  },

  // Plaid-related queries
  plaid: {
    connections: (estateId: number) => ["plaidConnections", estateId] as const,
    transactions: (estateId: number, connectionId: number) =>
      ["plaidTransactions", estateId, connectionId] as const,
  },

  // Calendly-related queries
  calendly: {
    booking: (estateId: number, source: string) => ["calendlyBooking", estateId, source] as const,
  },

  // Admin-related queries
  admin: {
    users: ["adminUsers"] as const,
    requests: ["adminRequests"] as const,
    dashboard: ["adminDashboard"] as const,
    featureFlags: ["adminFeatureFlags"] as const,
  },

  // Feature flags and settings
  features: {
    flags: ["featureFlags"] as const,
    subscription: ["subscription"] as const,
  },

  // Auth-related queries
  auth: {
    user: ["authUser"] as const,
    session: ["authSession"] as const,
  },
} as const;

// Helper function to get all related query keys for a data type
export const getRelatedQueryKeys = {
  taskUpdate: (estateId?: number) => [
    queryKeys.tasks.person(estateId),
    queryKeys.tasks.featured,
    queryKeys.user.settings,
    queryKeys.person.single(estateId!),
  ],

  userUpdate: (userId: string) => [
    queryKeys.user.current,
    queryKeys.user.profile(userId),
    queryKeys.user.settings,
  ],

  documentUpdate: (estateId: number) => [
    queryKeys.documents.person(estateId),
    queryKeys.documents.all,
    queryKeys.tasks.person(estateId), // Tasks may depend on documents
  ],

  personUpdate: (estateId: number) => [
    queryKeys.person.single(estateId),
    queryKeys.person.metadata(estateId),
    queryKeys.contacts.person(estateId),
    queryKeys.documents.person(estateId),
    queryKeys.tasks.person(estateId),
  ],
} as const;
